import { useContext, useEffect, useState } from "react";
import {
  getColumns,
  initialSort,
} from "../../components/ClientOverview/ClientInteractionOverviewTableConfig";
import { Box } from "@mui/system";
import { convertToUTC, getNormalizedTimeZone, getPresetDateRangecalendar } from "../../utils/utils";
import BasicDatatable from "../Common/BasicDatatable";
import ProgressSpinner from "../Common/ProgressSpinner";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ClientService from "../../services/clients/ClientService";
import SlideOver from "../Common/SlideOver";
import ClientInteractionActionView from "./ClientInteractionActionView";
import { Tab, Tabs } from "@mui/material";
import Filter from "../FilterComponents/Filter";
import { format } from "date-fns";
import { GlobalContext } from "../../context/GlobalContext";
import UserTeam from "../Analytics/UserTeam";
import useGlobalContext from "../../context/useGlobalContext";
import { getUserAttributes } from "../../utils/auth";

const generateRows = (
  accountInsights: any[],
  startEndMeetingTime: any,
  isTeamMeeting: boolean,
  isClientInteraction: boolean,
  slideOverParent: string,
  onClientClick: (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string) => void
) =>
  accountInsights?.map((item: any) => {
    return {
      meeting_id: item.interactionId,
      title: item.title,
      clientDomain: item.clientDomain,
      clientName: item.clientName,
      totalMrr: item.totalMrr,
      date: item.interactionStartTime,
      duration: item.duration,
      rating: item.overallSentiment,
      analystTime: item.analystTime,
      managerTime: item.managerTime,
      analysis_source: item.ratingSource,
      startEndMeetingTime,
      onClientClick,
      isTeamMeeting: isTeamMeeting,
      isClientInteraction: isClientInteraction,
      slideOverParent: slideOverParent,
      clientOrgId: item?.clientOrgId,
      sentimentSummary: item.sentimentSummary,
      hasAsset: item.hasAsset,
      subOrgId: item.subOrgId,
      isPresentAsset: item.isPresentAsset,
      isPreviousAsset: item.isPreviousAsset,
    };
  });

interface InteractionQualitySlideoverClientProps {
  isSlideOverOpen: boolean;
  slideOverParent?: string;
  filterKey: string;
  filterReset?: number;
  parentPageIdentifier?: string;
  customDateRange?: {
    startDate: string;
    endDate: string;
} | undefined;
}
interface FilterValues {
    searchText: string;
    filterBy: string;
}

const ClientInteractionSlideOverview = ({
  isSlideOverOpen,
  filterKey,
  slideOverParent,
  filterReset,
  parentPageIdentifier,
  customDateRange
}: InteractionQualitySlideoverClientProps) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [sort, setSort] = useState(initialSort);
  const { showSnackbar } = useDisplaySnackbar();
  const [accountInsights, setAccountInsights] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [tabValue, setTabValue] = useState<string>("interaction");
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };
  const removeCustomDateRange = true;
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [clientDomain, setClientDomain] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");
  const [filtersModified, setFiltersModified] = useState(false);
  const { startEndMeetingTime } = useContext(GlobalContext);
  const effectiveRange = customDateRange
  ? {
      start_time: customDateRange.startDate,
      end_time: customDateRange.endDate,
    }
  : getPresetDateRangecalendar("lastWeek");
  const [clientOrgId, setClientOrgId] = useState<string>("");
  const [subOrgId, setSubOrgId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const loggedInEmail = getUserAttributes()?.email;
  const { accountDetails } = useGlobalContext();
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";

  const getDefaultFilters = (filterKey: string): FilterValues[] => {
    switch (filterKey) {
      case "explicitagenda":
        return [{ filterBy: "explicitagenda", searchText: "explicitagenda" }];
      case "clientnoshow":
        return [{ filterBy: "analysis_source", searchText: "clientnoshow" }];
      case "accountnoshow":
        return [{ filterBy: "analysis_source", searchText: "accountnoshow" }];
      case "clientshow":
        return [{filterBy: "analysis_source", searchText: "clientshow" }];
      case "noexplicitagenda":
        return [{ filterBy: "explicitagenda", searchText: "noexplicitagenda"}]
      case "accountshow":
        return [{ filterBy: "analysis_source", searchText: "accountshow" }];
      default:
        return [];
    }
  };
  const defaultFilters: FilterValues[] = getDefaultFilters(filterKey);
  const [tableFilter, setTableFilter] = useState<FilterValues>({
    searchText: "",
    filterBy: "",
  });
  const [tableFilters, setTableFilters] = useState<FilterValues[]>([]);

  useEffect(() => {
    if (!filtersModified) {
      setTableFilters(defaultFilters);
    }
  }, [filterKey, filtersModified]);

  const handleFilterChange = (filter: FilterValues) => {
    setFiltersModified(true);
    setTableFilters(prev => {
      const withoutSameGroup = prev.filter(
        f => f.filterBy !== filter.filterBy
      );
      return [...withoutSameGroup, filter];
    });
    setTableFilter({ searchText: "", filterBy: "" });
  };
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dropDownOptions = [
    {
      label: "Rating Source",
      value: "analysis_source",
      options: [
        { label: "Client No Show", value: "clientnoshow" },
        { label: "Rep No Show", value: "accountnoshow" },
        { label: "Client Show", value: "clientshow" },
        { label: "Rep Show", value: "accountshow" },
      ],
    },
    {
      label: "Explicit Agenda",
      value: "explicitagenda",
      options: [
        { label: "Yes", value: "explicitagenda" },
        { label: "No", value: "noexplicitagenda" },
      ],
    }
  ];  
  
  const apiFilters = tableFilters.reduce((acc, filter) => {
    if (filter.filterBy && filter.searchText) {
      acc[filter.filterBy] = filter.searchText;
    }
    return acc;
  }, {} as Record<string, string>);
  const finalApiFilters = filtersModified ? apiFilters : getDefaultFilters(filterKey).reduce((acc, f) => {
    acc[f.filterBy] = f.searchText;
    return acc;
  }, {} as Record<string, string>);

  const filterArray = Object.values(finalApiFilters);
  const columns = getColumns();

  useEffect(() => {
    if (!isSlideOverOpen) {
      setFiltersModified(false);
      setSlideOverOpen(false);
      setClientDomain("");
      setClientOrgId("");
    }
  }, [isSlideOverOpen]);
  
  const {
    isLoading: isInteractionLoading,
    isSuccess: isInteractionSuccess,
    isError: isInteractionError,
    error: interactionError,
    data: interactionData,
  } = ClientService.useGetInteractionQuality(
    {
      startTime: effectiveRange?.start_time
      ? convertToUTC(effectiveRange.start_time)
      : convertToUTC(startEndMeetingTime?.start_time),
      endTime: effectiveRange?.end_time
      ? convertToUTC(effectiveRange.end_time)
      : convertToUTC(startEndMeetingTime?.end_time),
      offset: currentPageIndex,
      limit: pageSize,
      sortBy: sort.sort_field,
      sortOrder: sort.sort_order,
      filters: filterArray,
      employeePorId: selectedUser?.employeePorId || loggedInProfileOrgId,
      timeZone: getNormalizedTimeZone(),
    },
      isSlideOverOpen
  );

  // handle api data
  useEffect(() => {
  if (interactionData && isInteractionSuccess) {
    const clientInteractionList = interactionData.data || [];
    setAccountInsights(clientInteractionList);
    setTotalRecord(interactionData.totalRecords || clientInteractionList.length);
  } else if (interactionError) {
    setAccountInsights([]);
    showSnackbar("error", interactionError?.message || "Failed to load data.");
  }
}, [interactionData, isInteractionError, interactionError, isInteractionSuccess]);

  // paginate + generate rows
  useEffect(() => {
    const filteredRows = generateRows(
      accountInsights,
      effectiveRange,
      true,
      true,
      "client-interaction-overview",
      onClientClick,
    );

    setRows(filteredRows);
  }, [accountInsights]);

  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageIndex(1);
    setPageSize(newPageSize);
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      setSort({
        sort_field: sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
    }
  };

  const onClientClick = (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string) => {
    setClientDomain(clientDomain);
    setClientName(clientName);
    setSlideOverOpen(slideOver);
    setClientOrgId(clientOrgId);
    setSubOrgId(subOrgId);
  };

  const formattedEndDate = customDateRange?.endDate
    ? format(new Date(customDateRange.endDate), "MMM dd yyyy")
    : null;

  return (
    <div>
      <Box sx={{ width: "100%", height: "100%" }}>
        <Box className="client-business-overview">
          <Box className="client-interaction__tabs-container">
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Tabs value={tabValue} onChange={(_e, val) => handleTabChange(_e, val)}>
                <Tab label="Interaction" value="interaction" />
              </Tabs>
              <UserTeam
                setSelectedUser={setSelectedUser}
                display={false}
                loggedInEmail={loggedInEmail}
                loggedInProfileOrgId={loggedInProfileOrgId}
                showDropdown={true}
                showDomainFirst={false}
                parent_page="client_stats"
              />
              <Filter
                onFilter={handleFilterChange}
                filterOpenState={{ isFilterOpen, setIsFilterOpen }}
                filtervalues={tableFilter}
                dropdownOptions={dropDownOptions}
                setTableFilters={setTableFilters}
                initialAppliedFilters={tableFilters}
                setFiltersModified={setFiltersModified}
                resetSignal={filterReset}
                singleFilterMode={false}
              />
            </Box>
            {formattedEndDate && (
              <span className="client-business__daterange__label">
                <h1>
                  Week ending{" "}
                  <span className="client-business__daterange__date">{formattedEndDate}</span>
                </h1>
              </span>
            )}
          </Box>
          <Box className="client-business-overview__content">
            {tabValue === "interaction"  && (
              <>
              <BasicDatatable
                columns={columns}
                rows={rows}
                rowCount={totalRecord}
                initialSort={initialSort}
                onPageSizeChange={(newPageSize) => {
                  if (newPageSize) handlePageSizeChange(newPageSize);
                }}
                onSortingChange={onSortingChange}
                onPaginationChange={(newPageIndex: number) =>
                  setCurrentPageIndex(newPageIndex + 1)
                }
                minHeight="400px"
                isBordervisible={true}
              />
              <ProgressSpinner showSpinner={isInteractionLoading} />
              </>
            )}
          </Box>
        </Box>
      </Box>
      <SlideOver isOpen={slideOverOpen} onClose={() => setSlideOverOpen(false)}>
        <ClientInteractionActionView
          clientDomain={clientDomain}
          isSlideOverOpen={isSlideOverOpen}
          removeCustomDateRange={removeCustomDateRange}
          slideOverParent={slideOverParent}
          parentPageIdentifier={parentPageIdentifier}
          relationshipType="client"
          appliedFilters={finalApiFilters}
          clientOrgId={clientOrgId}
          customDateRange={customDateRange}
          subOrgId={subOrgId}
          clientName={clientName}
        />
      </SlideOver>
    </div>
  );
};

export default ClientInteractionSlideOverview;
